import { useEffect, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Lightbox } from './Lightbox';
import type { LightboxCaption, LightboxImageSize } from './Lightbox';

const cheapPatriotImages: [string, string] = [
  'https://assets.salieflewis.com/cheap_patriot_1.jpg',
  'https://assets.salieflewis.com/cheap_patriot_2.jpg',
];

const cheapPatriotCaptions: [LightboxCaption, LightboxCaption] = [
  {
    title: 'Cheap Patriot (1)',
    year: '2026',
  },
  {
    title: 'Cheap Patriot (2)',
    year: '2026',
  },
];

const cheapPatriotImageSizes: [LightboxImageSize, LightboxImageSize] = [
  {
    width: 1800,
    height: 1350,
  },
  {
    width: 1350,
    height: 1800,
  },
];

function preloadImages(urls: string[]) {
  for (const url of urls) {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }
}

export function CheapPatriot() {
  const [show, setShow] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setShow(false);
    const timer = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const warmImages = () => preloadImages(cheapPatriotImages);

    if ('requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(warmImages);
      return () => window.cancelIdleCallback(idleCallback);
    }

    const timer = setTimeout(warmImages, 600);
    return () => clearTimeout(timer);
  }, []);

  const imageTrail = useTrail(1, {
    from: { opacity: 0 },
    to: show ? { opacity: 1 } : { opacity: 0 },
    config: { duration: 250 },
  });

  const handleImageClick = () => {
    setCurrentImageIndex(0);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setCurrentImageIndex(0);
  };

  return (
    <>
      <div className="relative z-1 flex w-full flex-col items-center justify-center gap-8 bg-transparent p-0 md:flex-row md:gap-4">
        <div className="flex max-w-full flex-col items-center justify-center gap-10 p-4 md:flex-row md:gap-8">
          <animated.div
            className="relative z-1 w-[min(82vw,640px)] shrink-0 origin-center cursor-pointer transition-transform duration-500 ease-out md:w-[min(62vw,760px)] md:hover:scale-[1.02]"
            style={imageTrail[0]}
            onClick={handleImageClick}
          >
            <img
              src={cheapPatriotImages[0]}
              alt={cheapPatriotCaptions[0].title}
              width={cheapPatriotImageSizes[0].width}
              height={cheapPatriotImageSizes[0].height}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="block h-auto w-full object-contain md:max-h-[min(58vh,680px)] xl:max-h-[720px]"
            />
          </animated.div>
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox
          images={cheapPatriotImages}
          caption={cheapPatriotCaptions[0]}
          captions={cheapPatriotCaptions}
          imageSizes={cheapPatriotImageSizes}
          currentIndex={currentImageIndex}
          onClose={handleCloseLightbox}
          onNavigate={setCurrentImageIndex}
        />
      )}
    </>
  );
}
